import type {
  AcademicTerm,
  AttendanceRecord,
  CalendarEvent,
  Classroom,
  DailyRecord,
  HomeroomHomeworkRecord,
  HomeroomRecord,
  HomeworkAssignment,
  HomeworkRecord,
  QuizResponse,
  QuizSession,
  SeatingChart,
  Student,
  StudentRoutineItem,
} from "@classroom/domain";
import { compareStudentNumbers, paginateReportRows, studentNumberLabel } from "@classroom/domain";
import { i18n } from "../i18n";
import { homeroomHomeworkTaskLabel } from "../i18n/labels";
import { assignmentRecordLabels } from "./assignmentRecordLabels";
import { encodeReportCsv, encodeReportJson, type RecordFileFormat } from "./recordExportFormats";

export type RecordExportTab =
  | "records"
  | "homework"
  | "attendance"
  | "homeroomHomework"
  | "studentRecords"
  | "homeroom"
  | "homeroomDuties"
  | "seating"
  | "calendar"
  | "quiz"
  | "roster";
export type ExportFormat = "docx" | RecordFileFormat;
type Sheet = { title: string; headers: string[]; rows: string[][]; repeatColumns?: number[] };
export type RecordReport = {
  title: string;
  className: string;
  landscape?: boolean;
  sheets: Sheet[];
  seating?: { chart?: SeatingChart; students: Student[] };
};
type Source = {
  students: Student[];
  dailyRecords: DailyRecord[];
  homeworkAssignments: HomeworkAssignment[];
  homeworkRecords: HomeworkRecord[];
  attendanceRecords: AttendanceRecord[];
  homeroomHomeworkRecords: HomeroomHomeworkRecord[];
  homeroomRecords: HomeroomRecord[];
  studentRoutineItems: StudentRoutineItem[];
  seatingCharts: SeatingChart[];
  calendarEvents: CalendarEvent[];
  quizSessions: QuizSession[];
  quizResponses: QuizResponse[];
};

const titles: Record<RecordExportTab, string> = {
  get records() {
    return i18n.t("marketing:studentsTab");
  },
  get homework() {
    return i18n.t("localExport:recordTypes.homework");
  },
  get attendance() {
    return i18n.t("adminRecords:kinds.attendance");
  },
  get homeroomHomework() {
    return i18n.t("guidedTour:steps.demoHomeroomHomework.title");
  },
  get studentRecords() {
    return i18n.t("marketing:studentsTab");
  },
  get homeroom() {
    return i18n.t("marketing:homeroomTab");
  },
  get homeroomDuties() {
    return i18n.t("marketing:dutiesTab");
  },
  get seating() {
    return i18n.t("marketing:seatingTab");
  },
  get calendar() {
    return i18n.t("adminRecords:kinds.calendar");
  },
  get quiz() {
    return i18n.t("localExport:recordTypes.quiz");
  },
  get roster() {
    return i18n.t("adminRecords:kinds.roster");
  },
};
const sheet = (title: string, headers: string[], rows: Array<Array<string | number | undefined | null>>): Sheet => ({
  title,
  headers,
  rows: rows.map((row) => row.map((cell) => String(cell ?? ""))),
});
const sorted = <T extends { date: string }>(records: T[]) => [...records].sort((a, b) => a.date.localeCompare(b.date));

export function buildRecordReport(
  tab: RecordExportTab,
  classItem: Classroom,
  source: Source,
  selectedStudentId?: string,
  term?: AcademicTerm,
): RecordReport {
  const classStudents = source.students
    .filter((s) => s.classId === (classItem.sharedDataClassId || classItem.id))
    .sort(compareStudentNumbers);
  const students =
    tab === "studentRecords" && selectedStudentId
      ? classStudents.filter((s) => s.id === selectedStudentId)
      : classStudents;
  const names = new Map(classStudents.map((s) => [s.id, `${studentNumberLabel(s)} ${s.name}`]));
  const name = (id: string) => names.get(id) || i18n.t("reportLabels:removedStudent");
  const inClass = <T extends { classId: string }>(items: T[]) => items.filter((item) => item.classId === classItem.id);
  let sheets: Sheet[] = [];
  if (tab === "records") {
    const daily = sorted(inClass(source.dailyRecords));
    sheets = [
      sheet(
        i18n.t("adminRecords:kinds.materials"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("classroomPerformance:students.missingMaterials"),
          i18n.t("landing:typeNotes"),
        ],
        daily.flatMap((record) =>
          Object.entries(record.students)
            .filter(([, value]) => value.missingMaterials)
            .map(([studentId, value]) => [
              record.date,
              name(studentId),
              i18n.t("classroomPerformance:students.missingMaterials"),
              value.note,
            ]),
        ),
      ),
      sheet(
        i18n.t("landing:typeAnswers"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("reportLabels:answerCount"),
          i18n.t("landing:typeNotes"),
        ],
        daily.flatMap((record) =>
          Object.entries(record.students)
            .filter(([, value]) => value.answers > 0)
            .map(([studentId, value]) => [record.date, name(studentId), value.answers, value.note]),
        ),
      ),
    ];
  } else if (tab === "homework") {
    const assignments = inClass(source.homeworkAssignments).sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    const records = new Map(inClass(source.homeworkRecords).map((record) => [record.assignmentId, record]));
    sheets = [
      sheet(
        i18n.t("localExport:recordTypes.homework"),
        [i18n.t("reportLabels:dueDate"), i18n.t("screenCopy:calendarImportDialog.event"), i18n.t("landing:typeNotes")],
        assignments.map((item) => [item.dueDate, item.title, item.note]),
      ),
      sheet(
        i18n.t("reportLabels:homeworkSubmissionRecords"),
        [
          i18n.t("reportLabels:dueDate"),
          i18n.t("screenCopy:calendarImportDialog.event"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("screenCopy:teacherConsole.status"),
          i18n.t("screenCopy:assignmentRecords.score"),
          i18n.t("landing:typeNotes"),
        ],
        assignments.flatMap((item) => {
          const labels = assignmentRecordLabels(item.category);
          const byStudent = records.get(item.id)?.students || {};
          return students.map((student) => {
            const entry = byStudent[student.id];
            return [
              item.dueDate,
              item.title,
              name(student.id),
              entry ? labels.statuses[entry.status] : i18n.t("gradeOverview:dashboard.unrecorded"),
              entry?.score,
              entry?.note,
            ];
          });
        }),
      ),
    ];
  } else if (tab === "attendance") {
    sheets = [
      sheet(
        i18n.t("screenCopy:teacherConsole.attendanceRecords"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("screenCopy:teacherConsole.status"),
        ],
        sorted(inClass(source.attendanceRecords)).flatMap((record) =>
          record.studentIds.map((id) => [
            record.date,
            name(id),
            record.exceptions[id] === "absent"
              ? i18n.t("attendanceRegister:absent")
              : record.exceptions[id] === "late"
                ? i18n.t("screenCopy:teacherConsole.late")
                : i18n.t("attendanceRegister:present"),
          ]),
        ),
      ),
    ];
  } else if (tab === "homeroomHomework") {
    sheets = [
      sheet(
        i18n.t("guidedTour:steps.demoHomeroomHomework.title"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("interfaceExtras:subject"),
          i18n.t("screenCopy:teacherConsole.type"),
          i18n.t("screenCopy:teacherConsole.taskDescription"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("screenCopy:teacherConsole.status"),
        ],
        sorted(inClass(source.homeroomHomeworkRecords)).flatMap((record) =>
          record.missingStudentIds.map((id) => [
            record.date,
            record.subject,
            homeroomHomeworkTaskLabel(record.taskType),
            record.taskDetail,
            name(id),
            i18n.t("interfaceExtras:missingLabel"),
          ]),
        ),
      ),
    ];
  } else if (tab === "studentRecords") {
    const attendance = sorted(inClass(source.attendanceRecords));
    const homework = sorted(inClass(source.homeroomHomeworkRecords));
    const routine = inClass(source.studentRoutineItems);
    sheets = [
      sheet(
        i18n.t("screenCopy:teacherConsole.absentDates"),
        [i18n.t("rosterAdmin:number"), i18n.t("drawHistory:name"), i18n.t("screenCopy:teacherConsole.absentDates")],
        students.flatMap((student) =>
          attendance
            .filter((item) => item.studentIds.includes(student.id) && item.exceptions[student.id] === "absent")
            .map((item) => [studentNumberLabel(student), student.name, item.date]),
        ),
      ),
      sheet(
        i18n.t("guidedTour:steps.demoHomeroomHomework.title"),
        [
          i18n.t("rosterAdmin:number"),
          i18n.t("drawHistory:name"),
          i18n.t("classroomPerformance:students.date"),
          i18n.t("interfaceExtras:subject"),
          i18n.t("screenCopy:teacherConsole.type"),
          i18n.t("screenCopy:teacherConsole.taskDescription"),
        ],
        students.flatMap((student) =>
          homework
            .filter((item) => item.missingStudentIds.includes(student.id))
            .map((item) => [
              studentNumberLabel(student),
              student.name,
              item.date,
              item.subject,
              homeroomHomeworkTaskLabel(item.taskType),
              item.taskDetail,
            ]),
        ),
      ),
      sheet(
        i18n.t("cloudMigration:collections.studentRoutineItems"),
        [i18n.t("rosterAdmin:number"), i18n.t("drawHistory:name"), i18n.t("reportLabels:exceptionTask")],
        students.flatMap((student) =>
          routine
            .filter((item) => item.studentIds.includes(student.id))
            .map((item) => [studentNumberLabel(student), student.name, item.title]),
        ),
      ),
    ];
  } else if (tab === "homeroom") {
    sheets = [
      sheet(
        i18n.t("marketing:homeroomTab"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("screenCopy:calendarImportDialog.event"),
          i18n.t("landing:typeNotes"),
        ],
        sorted(inClass(source.homeroomRecords)).flatMap((record) =>
          record.affairs.map((item) => [record.date, item.title, item.note]),
        ),
      ),
    ];
  } else if (tab === "homeroomDuties") {
    const current = Object.entries(classItem.dutyAssignments || {});
    const history = sorted(inClass(source.homeroomRecords)).flatMap((record) =>
      Object.entries(record.duties).map(([duty, studentId]) => [record.date, duty, name(studentId)]),
    );
    sheets = [
      sheet(
        i18n.t("reportLabels:currentRoles"),
        [i18n.t("interfaceExtras:duty"), i18n.t("reportLabels:classNumberAndName")],
        current.map(([duty, studentId]) => [duty, name(studentId)]),
      ),
      sheet(
        i18n.t("reportLabels:roleHistory"),
        [
          i18n.t("classroomPerformance:students.date"),
          i18n.t("interfaceExtras:duty"),
          i18n.t("reportLabels:classNumberAndName"),
        ],
        history,
      ),
    ];
  } else if (tab === "seating") {
    const chart = source.seatingCharts.find((item) => item.classId === (classItem.sharedDataClassId || classItem.id));
    sheets = [
      sheet(
        i18n.t("marketing:seatingTab"),
        [
          i18n.t("screenCopy:teacherConsole.rows_2a2b7"),
          i18n.t("screenCopy:teacherConsole.columns_ad3ee"),
          i18n.t("reportLabels:classNumberAndName"),
        ],
        chart
          ? Array.from({ length: chart.rows * chart.columns }, (_, index) => [
              Math.floor(index / chart.columns) + 1,
              (index % chart.columns) + 1,
              chart.seats[`${Math.floor(index / chart.columns)}_${index % chart.columns}`]
                ? name(chart.seats[`${Math.floor(index / chart.columns)}_${index % chart.columns}`])
                : i18n.t("localExport:printEmptySeat"),
            ])
          : [],
      ),
    ];
  } else if (tab === "calendar") {
    const types: Record<CalendarEvent["type"], string> = {
      school: i18n.t("reportLabels:school"),
      homework: i18n.t("interfaceExtras:homework"),
      assessment: i18n.t("reportLabels:assessment"),
      dictation: i18n.t("interfaceExtras:dictation"),
      reminder: i18n.t("screenCopy:teacherConsole.reminder"),
    };
    sheets = [
      sheet(
        i18n.t("adminRecords:kinds.calendar"),
        [
          i18n.t("terms:start"),
          i18n.t("terms:end"),
          i18n.t("screenCopy:calendarImportDialog.event"),
          i18n.t("gradeOverview:table.categoryFilter"),
          i18n.t("landing:typeNotes"),
          i18n.t("reportLabels:source"),
        ],
        sorted(source.calendarEvents.filter((item) => !item.classId || item.classId === classItem.id)).map((item) => [
          item.date,
          item.endDate,
          item.title,
          types[item.type],
          item.note,
          item.source?.sourceName,
        ]),
      ),
    ];
  } else if (tab === "quiz") {
    const sessions = inClass(source.quizSessions).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
    const sessionsById = new Map(sessions.map((item) => [item.id, item]));
    sheets = [
      sheet(
        i18n.t("reportLabels:quizSessions"),
        [
          i18n.t("reportLabels:createdAt"),
          i18n.t("reportLabels:quiz"),
          i18n.t("reportLabels:code"),
          i18n.t("screenCopy:teacherConsole.status"),
          i18n.t("screenCopy:teacherConsole.numberOfQuestions"),
        ],
        sessions.map((item) => [
          item.createdAt.slice(0, 16).replace("T", " "),
          item.title,
          item.code,
          item.status === "open"
            ? i18n.t("screenCopy:teacherConsole.inProgress")
            : i18n.t("screenCopy:teacherConsole.ended"),
          item.questionCount,
        ]),
      ),
      sheet(
        i18n.t("reportLabels:responseScores"),
        [
          i18n.t("reportLabels:quiz"),
          i18n.t("reportLabels:classNumberAndName"),
          i18n.t("screenCopy:assignmentRecords.score"),
          i18n.t("screenCopy:teacherConsole.numberOfQuestions"),
          i18n.t("reportLabels:submittedAt"),
          i18n.t("screenCopy:teacherConsole.status"),
        ],
        inClass(source.quizResponses)
          .filter((item) => sessionsById.has(item.sessionId))
          .sort((a, b) => a.submittedAt.localeCompare(b.submittedAt))
          .map((item) => [
            sessionsById.get(item.sessionId)?.title,
            name(item.studentId),
            item.score,
            item.questionCount,
            item.submittedAt.slice(0, 16).replace("T", " "),
            item.completionStatus,
          ]),
      ),
    ];
  } else {
    sheets = [
      sheet(
        i18n.t("adminRecords:kinds.roster"),
        [
          i18n.t("rosterAdmin:number"),
          i18n.t("drawHistory:name"),
          i18n.t("rosterAdmin:gender"),
          i18n.t("screenCopy:teacherConsole.status"),
        ],
        students.map((student) => [
          studentNumberLabel(student),
          student.name,
          (
            {
              M: i18n.t("rosterAdmin:male"),
              F: i18n.t("rosterAdmin:female"),
              X: i18n.t("interfaceExtras:other"),
              "": "",
            } as Record<string, string>
          )[student.gender],
          student.active ? i18n.t("screenCopy:teacherConsole.active") : i18n.t("terms:disabled"),
        ]),
      ),
    ];
  }
  const student = classStudents.find((item) => item.id === selectedStudentId);
  return {
    title: `${student ? `${studentNumberLabel(student)} ${student.name}｜${titles[tab]}` : titles[tab]}${term ? `｜${term.schoolYear} ${term.name}（${i18n.t("adminRecords:dateRange", { from: term.startDate, to: term.endDate })}）` : ""}`,
    className: classItem.name,
    sheets,
    seating:
      tab === "seating"
        ? {
            chart: source.seatingCharts.find((item) => item.classId === (classItem.sharedDataClassId || classItem.id)),
            students,
          }
        : undefined,
  };
}

function download(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function exportRecordReport(report: RecordReport, format: ExportFormat) {
  const filename = recordFilename(report);
  if (format === "csv" || format === "json") {
    const content = format === "csv" ? encodeReportCsv(report) : encodeReportJson(report);
    const type = format === "csv" ? "text/csv;charset=utf-8" : "application/json;charset=utf-8";
    download(new Blob([content], { type }), `${filename}.${format}`);
  } else if (format === "xlsx") {
    const ExcelJS = (await import("exceljs")).default;
    const workbook = new ExcelJS.Workbook();
    const worksheetNames = new Set<string>();
    for (const section of report.sheets) {
      const baseName =
        section.title
          .replace(/[\\/*?:[\]]/g, "-")
          .replace(/^'+|'+$/g, "")
          .slice(0, 31) || i18n.t("screenCopy:recordImportPanel.record");
      let sheetName = baseName;
      let index = 2;
      while (worksheetNames.has(sheetName.toLocaleLowerCase())) {
        const suffix = ` (${index++})`;
        sheetName = baseName.slice(0, 31 - suffix.length) + suffix;
      }
      worksheetNames.add(sheetName.toLocaleLowerCase());
      const ws = workbook.addWorksheet(sheetName);
      ws.addRow(section.headers);
      ws.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
      ws.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF317960" } };
      ws.views = [{ state: "frozen", ySplit: 1 }];
      for (const row of section.rows) ws.addRow(row.map((cell) => (/^[\s]*[=+\-@]/.test(cell) ? `'${cell}` : cell)));
      ws.columns.forEach((column, index) => {
        column.width = Math.min(
          54,
          Math.max(
            14,
            section.headers[index].length * 2 + 4,
            ...section.rows.map((row) => Math.min(54, row[index]?.length || 0)),
          ),
        );
      });
      ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: section.headers.length } };
    }
    const buffer = await workbook.xlsx.writeBuffer();
    download(
      new Blob([buffer as BlobPart], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }),
      `${filename}.xlsx`,
    );
  } else if (format === "docx") {
    const { Document, HeadingLevel, Packer, Paragraph, Table, TableCell, TableRow, TextRun, WidthType } = await import(
      "docx"
    );
    const children: Array<InstanceType<typeof Paragraph> | InstanceType<typeof Table>> = [
      new Paragraph({ text: `${report.className}｜${report.title}`, heading: HeadingLevel.TITLE }),
    ];
    for (const section of report.sheets) {
      children.push(new Paragraph({ text: section.title, heading: HeadingLevel.HEADING_2 }));
      const rows = [
        section.headers,
        ...(section.rows.length
          ? section.rows
          : [[i18n.t("reportLabels:noSavedRecords"), ...section.headers.slice(1).map(() => "")]]),
      ];
      children.push(
        new Table({
          width: { size: 100, type: WidthType.PERCENTAGE },
          rows: rows.map(
            (row, rowIndex) =>
              new TableRow({
                children: row.map(
                  (cell) =>
                    new TableCell({
                      children: [
                        new Paragraph({
                          children: [
                            new TextRun({ text: cell, bold: rowIndex === 0, font: "Microsoft JhengHei", size: 18 }),
                          ],
                        }),
                      ],
                    }),
                ),
              }),
          ),
        }),
      );
      children.push(new Paragraph({ text: "" }));
    }
    const document = new Document({
      sections: [{ properties: { page: { size: { orientation: "landscape" } } }, children }],
    });
    download(await Packer.toBlob(document), `${filename}.docx`);
  } else {
    downloadRecordPdf(await createRecordPdf(report));
  }
}

function recordFilename(report: RecordReport) {
  return `${report.className}-${report.title}-${new Date().toLocaleDateString("sv-SE")}`.replace(/[\\/:*?"<>|]/g, "-");
}

export type RecordPdf = { blob: Blob; filename: string };

export function downloadRecordPdf(file: RecordPdf) {
  download(file.blob, file.filename);
}

export async function createRecordPdf(report: RecordReport): Promise<RecordPdf> {
  const filename = `${recordFilename(report)}.pdf`;
  const { PDFDocument, PageSizes } = await import("pdf-lib");
  const pdf = await PDFDocument.create();
  async function addCanvasPage(canvas: HTMLCanvasElement, width: number, height: number) {
    const image = await pdf.embedPng(canvas.toDataURL("image/png"));
    pdf.addPage([width, height]).drawImage(image, { x: 0, y: 0, width, height });
  }
  async function savePdf(): Promise<RecordPdf> {
    const bytes = new Uint8Array(await pdf.save());
    return { blob: new Blob([bytes.buffer], { type: "application/pdf" }), filename };
  }
  if (report.seating) {
    const canvas = document.createElement("canvas");
    canvas.width = 1684;
    canvas.height = 1190;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error(i18n.t("reportLabels:thisBrowserDoesNotSupportThePDFCanvas"));
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1d3c33";
    ctx.font = 'bold 42px "PingFang TC", "Microsoft JhengHei", sans-serif';
    ctx.fillText(`${report.className}｜${i18n.t("localExport:recordTypes.seating")}`, 90, 105);
    const chart = report.seating.chart;
    if (chart) {
      const teacher = chart.view === "teacher";
      ctx.font = '25px "PingFang TC", "Microsoft JhengHei", sans-serif';
      ctx.fillStyle = "#566960";
      ctx.fillText(teacher ? i18n.t("localExport:printTeacher") : i18n.t("localExport:printStudent"), 90, 150);
      const left = 100,
        top = teacher ? 195 : 265,
        gridW = 1484,
        gridH = 730;
      const extraW = chart.columnAisles.length * 34;
      const extraH = chart.rowAisles.length * 32;
      const gap = 15;
      const cellW = (gridW - extraW - gap * (chart.columns - 1)) / chart.columns;
      const cellH = (gridH - extraH - gap * (chart.rows - 1)) / chart.rows;
      const studentById = new Map(report.seating.students.map((s) => [s.id, s]));
      for (let vr = 0; vr < chart.rows; vr++) {
        const row = teacher ? chart.rows - 1 - vr : vr;
        const rowsBefore = Array.from({ length: vr }, (_, i) => (teacher ? chart.rows - 2 - i : i));
        const y = top + vr * (cellH + gap) + rowsBefore.filter((i) => chart.rowAisles.includes(i)).length * 32;
        for (let vc = 0; vc < chart.columns; vc++) {
          const col = teacher ? chart.columns - 1 - vc : vc;
          const colsBefore = Array.from({ length: vc }, (_, i) => (teacher ? chart.columns - 2 - i : i));
          const x = left + vc * (cellW + gap) + colsBefore.filter((i) => chart.columnAisles.includes(i)).length * 34;
          const student = studentById.get(chart.seats[`${row}_${col}`]);
          ctx.fillStyle = student?.gender === "F" ? "#fff0f4" : student?.gender === "M" ? "#eaf4fc" : "#f8faf8";
          ctx.strokeStyle = "#bccbc2";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.roundRect(x, y, cellW, cellH, 12);
          ctx.fill();
          ctx.stroke();
          ctx.textAlign = "center";
          ctx.fillStyle = "#2e493e";
          ctx.font = 'bold 26px "PingFang TC", "Microsoft JhengHei", sans-serif';
          ctx.fillText(
            student ? `${studentNumberLabel(student)}  ${student.name}` : i18n.t("localExport:printEmptySeat"),
            x + cellW / 2,
            y + cellH / 2 + 10,
            cellW - 18,
          );
          ctx.textAlign = "left";
        }
      }
      const boardY = teacher ? 1050 : 192;
      ctx.fillStyle = "#245d4a";
      ctx.beginPath();
      ctx.roundRect(525, boardY, 630, 65, 12);
      ctx.fill();
      ctx.fillStyle = "#ffffff";
      ctx.textAlign = "center";
      ctx.font = 'bold 30px "PingFang TC", "Microsoft JhengHei", sans-serif';
      ctx.fillText(i18n.t("localExport:printBoard"), 840, boardY + 43);
      ctx.textAlign = "left";
    } else {
      ctx.fillStyle = "#566960";
      ctx.font = '30px "PingFang TC", "Microsoft JhengHei", sans-serif';
      ctx.fillText(i18n.t("localExport:printNoSeating"), 90, 220);
    }
    await addCanvasPage(canvas, 842, 595);
    return savePdf();
  }
  const [pageWidth, pageHeight] = report.landscape ? [PageSizes.A4[1], PageSizes.A4[0]] : PageSizes.A4;
  const scale = 2;
  const width = pageWidth * scale,
    height = pageHeight * scale,
    margin = 48 * scale;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error(i18n.t("reportLabels:thisBrowserDoesNotSupportThePDFCanvas"));
  const context = ctx;
  const lineHeight = 30,
    pad = 12;
  let page = 0,
    y = 0;
  async function newPage(title: string, headers: string[][]) {
    // Snapshot the previous page before reusing its canvas for the next one.
    if (page) await addCanvasPage(canvas, pageWidth, pageHeight);
    page++;
    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.fillStyle = "#1d3c33";
    context.font = 'bold 38px "PingFang TC", "Microsoft JhengHei", sans-serif';
    context.fillText(`${report.className}｜${report.title}`, margin, margin + 8);
    context.font = 'bold 26px "PingFang TC", "Microsoft JhengHei", sans-serif';
    context.fillText(title, margin, margin + 60);
    y = margin + 95;
    context.font = 'bold 21px "PingFang TC", "Microsoft JhengHei", sans-serif';
    drawRow(headers, headers.length, true);
  }
  function wrap(value: string, maxWidth: number) {
    const lines: string[] = [];
    for (const paragraph of String(value).split(/\r?\n/)) {
      let line = "";
      for (const char of paragraph) {
        if (line && context.measureText(line + char).width > maxWidth) {
          lines.push(line);
          line = char;
        } else line += char;
      }
      lines.push(line);
    }
    return lines;
  }
  function drawRow(columns: string[][], count: number, header = false) {
    const w = (width - margin * 2) / count;
    const countLines = Math.max(...columns.map((lines) => lines.length));
    const h = Math.max(44, countLines * lineHeight + pad * 2);
    context.fillStyle = header ? "#317960" : page % 2 ? "#ffffff" : "#f7f9f5";
    context.fillRect(margin, y, w * count, h);
    context.fillStyle = header ? "#ffffff" : "#263a33";
    context.font = `${header ? "bold " : ""}21px "PingFang TC", "Microsoft JhengHei", sans-serif`;
    columns.forEach((lines, index) =>
      lines.forEach((line, offset) =>
        context.fillText(line, margin + w * index + pad, y + pad + 22 + offset * lineHeight),
      ),
    );
    context.strokeStyle = "#dce5df";
    context.beginPath();
    context.moveTo(margin, y + h);
    context.lineTo(margin + w * count, y + h);
    context.stroke();
    y += h;
  }
  for (const section of report.sheets) {
    const w = (width - margin * 2) / section.headers.length;
    context.font = 'bold 21px "PingFang TC", "Microsoft JhengHei", sans-serif';
    const headers = section.headers.map((cell) => wrap(cell, w - pad * 2));
    const headerHeight = Math.max(44, Math.max(...headers.map((cell) => cell.length)) * lineHeight + pad * 2);
    const rows = section.rows.length
      ? section.rows
      : [[i18n.t("reportLabels:noSavedRecords"), ...section.headers.slice(1).map(() => "")]];
    context.font = '21px "PingFang TC", "Microsoft JhengHei", sans-serif';
    const pages = paginateReportRows(
      rows.map((row) => row.map((cell) => wrap(cell, w - pad * 2))),
      {
        contentHeight: height - margin * 2 - 95 - headerHeight,
        lineHeight,
        padding: pad,
        repeatColumns: section.repeatColumns,
      },
    );
    for (const rows of pages) {
      await newPage(section.title, headers);
      for (const row of rows) drawRow(row, section.headers.length);
    }
  }
  if (page) await addCanvasPage(canvas, pageWidth, pageHeight);
  return savePdf();
}
